import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert2';
import Workflow from '../../../components/Workflow';
import { useAgents } from '../../../contexts/AgentsContext';
import { AppContext } from '../../../contexts/AppContext';
import { RunWorkflowDialog } from "../../../components/Dialogs";
import GenerateWorkflowDialog from '../../../components/Workflows/GenerateWorkflowDialog';
import { Button } from '../../../components/StyledElements';
import { loadGeneratedWorkflowGraph, extractGeneratedGraph } from '../../../services/workflowGeneration';
import { resolveGeneratorClientId } from "../../../utils/workflowGenerationRequest";

export const WorkflowGraph = ({ style, onClose = () => { } }) => {
  const { updatedAgent, setUpdatedAgent } = useAgents();
  const { selectedClient } = useContext(AppContext);
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  // The graph editor captures its initial `workflow` at mount and ignores later changes, so a
  // generated graph is applied by remounting it rather than by swapping the prop.
  const [graphVersion, setGraphVersion] = useState(0);
  const navigate = useNavigate();

  if (!updatedAgent) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%" }}>
      <span>Loading workflow...</span>
    </div>;
  }

  const applyGeneratedWorkflow = async (saved, results) => {
    const publicId = saved?.public_id;

    setShowGenerateDialog(false);

    // The generate response carries the graph it built, which is the reliable source — the
    // saved record may not be readable back through the workflow endpoints.
    const inlineGraph = extractGeneratedGraph(results);

    if (inlineGraph) {
      setUpdatedAgent({ ...updatedAgent, workflow_graph: inlineGraph });
      setGraphVersion((version) => version + 1);
      swal.fire({
        title: "Workflow generated",
        text: `"${saved?.name ?? results?.name ?? "Workflow"}" was loaded into this agent. It replaces the graph below — click Save Changes to keep it on the agent.`,
        icon: "success",
      });
      return;
    }

    if (!publicId) {
      swal.fire({
        title: "Generated",
        text: "The workflow was generated but no graph or workflow id came back, so it could not be loaded here.",
        icon: "info",
      });
      return;
    }

    const loaded = await loadGeneratedWorkflowGraph(publicId);

    if (loaded?.graph) {
      setUpdatedAgent({ ...updatedAgent, workflow_graph: loaded.graph });
      setGraphVersion((version) => version + 1);
      swal.fire({
        title: "Workflow generated",
        text: `"${saved?.name ?? "Workflow"}" was created and loaded into this agent. It replaces the graph below — click Save Changes to keep it on the agent.`,
        icon: "success",
      });
      return;
    }

    const result = await swal.fire({
      title: "Workflow generated",
      text: `"${saved?.name ?? "Workflow"}" was created, but its graph could not be loaded into this agent. Open it in the workflow editor instead?`,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Open workflow",
      cancelButtonText: "Stay here",
    });

    if (result.isConfirmed) {
      navigate(`/workflow/${publicId}`);
    }
  };

  return (
    <div style={{ position: "relative", height: "100%", width: "100%", display: "flex", flexDirection: "column", gap: "10px", ...style }}>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px" }}>
        <div style={{ fontSize: "13px", color: "#a0a6b1", flex: 1 }}>
          Describe the workflow in plain language and let the generator build the graph.
        </div>
        <Button onClick={() => setShowGenerateDialog(true)}>Generate from description</Button>
      </div>

      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        <Workflow
          key={graphVersion}
          workflow={{ ...updatedAgent.workflow_graph }}
          onWorkflowChange={(graph) => {
            const newAgent = { ...updatedAgent };
            newAgent.workflow_graph = graph;
            setUpdatedAgent(newAgent);
          }}
          onRunWorkflow={() => {
            console.log("!!! RUNNING WORKFLOW");
            // Here you would typically call a service to run the workflow
            // For example:
            if (updatedAgent.public_id) {
              setShowRunDialog(true);

              /*runAgentWorkflowGraph(updatedAgent.public_id, "THIS IS THE TEST PAYLOAD").then((results) => {
                ;
                console.log("!!! RUN AGENT WORKFLOW RESULTS: ", results);
              })*/
            }
          }}
        />
      </div>

      <GenerateWorkflowDialog
        show={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
        onGenerated={applyGeneratedWorkflow}
        defaultName={updatedAgent?.name ? `${updatedAgent.name} workflow` : ""}
        defaultDescription={updatedAgent?.description ?? ""}
        defaultPurpose={updatedAgent?.purpose ?? ""}
        clientId={resolveGeneratorClientId(selectedClient, updatedAgent)}
      />

      {showRunDialog &&
        <RunWorkflowDialog workflowGraph={updatedAgent.workflow_graph} agent={updatedAgent} show={true} onClose={() => setShowRunDialog(false)} />
      }
    </div>
  );
};

export default WorkflowGraph;
